import React, { useRef, useState } from "react";

/**
 * Side-by-side OR slider comparison of VORHER (before) and NACHHER (after) images.
 * Both labels are always visible to avoid confusion.
 */
const BeforeAfterSlider = ({ before, after, title, description, aspectClass = "aspect-[4/3]" }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(percent);
  };

  return (
    <div className="group" data-testid={`ba-${title?.toLowerCase?.()?.replace(/[^a-z0-9]+/g, "-")}`}>
      <div
        ref={containerRef}
        className={`relative w-full ${aspectClass} overflow-hidden select-none cursor-ew-resize border border-zinc-200`}
        onMouseMove={(e) => e.buttons === 1 && handleMove(e.clientX)}
        onMouseDown={(e) => handleMove(e.clientX)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
      >
        {/* After (full background) */}
        <img src={after} alt={`Nachher - ${title}`} className="absolute inset-0 w-full h-full object-cover" draggable={false} />

        {/* Before clipped */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <img src={before} alt={`Vorher - ${title}`} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
        </div>

        {/* Labels */}
        <span className="absolute top-4 left-4 bg-zinc-900 text-white text-xs font-bold tracking-widest uppercase px-3 py-1.5 select-none pointer-events-none">
          Vorher
        </span>
        <span className="absolute top-4 right-4 bg-red-600 text-white text-xs font-bold tracking-widest uppercase px-3 py-1.5 select-none pointer-events-none">
          Nachher
        </span>

        {/* Divider line + handle */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white shadow-2xl pointer-events-none"
          style={{ left: `${pos}%` }}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white border-2 border-red-600 flex items-center justify-center shadow-xl">
            <span className="text-red-600 font-bold text-lg leading-none">⇄</span>
          </div>
        </div>

        {/* Hidden range for accessibility */}
        <input
          type="range"
          min="0"
          max="100"
          value={pos}
          onChange={(e) => setPos(Number(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
          aria-label={`Vorher/Nachher Vergleich ${title}`}
        />
      </div>

      {(title || description) && (
        <div className="mt-5">
          {title && <h3 className="font-display font-bold text-xl text-zinc-900">{title}</h3>}
          {description && <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{description}</p>}
        </div>
      )}
    </div>
  );
};

export default BeforeAfterSlider;
